import {
  AlertTriangle,
  ArrowRight,
  Building2,
  CalendarClock,
  CreditCard,
  FileClock,
  FileText,
  Plus,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  NOME_TIPO_ATO,
  NOME_TIPO_SOCIETARIO,
  type AtoRecente,
  type ItemAgenda,
  type SituacaoAto,
  type SociedadeRecente,
  type TipoPendencia,
  type Urgencia,
} from '@social/shared';

/*
  As peças da tela de início.

  Separadas da página pelo motivo de sempre neste repositório: a página monta
  e busca; estas desenham. Juntas, o arquivo passaria das 600 linhas do lint na
  primeira coisa nova que a agenda aprender a mostrar.
*/

/**
 * Rótulo da situação do ato.
 *
 * Mora aqui, e não no domínio, pelo mesmo motivo do `TOM_SITUACAO` em
 * `sociedade.tsx`: é palavra de TELA. O servidor continua sendo a fonte da
 * situação; isto só a escreve em português de gente.
 */
const ROTULO_SITUACAO_ATO: Record<SituacaoAto, string> = {
  RASCUNHO: 'Rascunho',
  VALIDADO: 'Validado',
  GERADO: 'Documento gerado',
  ASSINADO: 'Assinado',
  PROTOCOLADO: 'Protocolado',
  REGISTRADO: 'Registrado',
  EXIGENCIA: 'Em exigência',
  CANCELADO: 'Cancelado',
};

/** Data ISO no formato que o contador lê. */
export function dataBR(iso: string): string {
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  return `${dia ?? ''}/${mes ?? ''}/${ano ?? ''}`;
}

/**
 * ATALHOS — o que a pessoa veio fazer.
 *
 * Três, e não oito: a tela inicial existe para tirar alguém do zero, e uma
 * grade de oito botões devolve à pessoa o mesmo problema do menu, só que com
 * mais tinta. Os três são as portas que um escritório usa toda semana.
 */
export function Atalhos(): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <Atalho
        para="/sociedades/nova"
        icone={Plus}
        titulo="Nova sociedade"
        detalhe="Constituição, do cadastro ao contrato pronto."
        destaque
      />
      <Atalho
        para="/pessoas/nova"
        icone={UserPlus}
        titulo="Nova pessoa"
        detalhe="Sócio ou administrador, para usar nos atos."
      />
      <Atalho
        para="/sociedades"
        icone={Building2}
        titulo="Ver sociedades"
        detalhe="A carteira inteira, com busca e filtros."
      />
    </div>
  );
}

function Atalho({
  para,
  icone: Icone,
  titulo,
  detalhe,
  destaque,
}: {
  para: string;
  icone: LucideIcon;
  titulo: string;
  detalhe: string;
  destaque?: boolean;
}): JSX.Element {
  return (
    <Link
      to={para}
      className={`group focus-visible:ring-marca-400 flex items-start gap-3 rounded-xl border p-4 transition-shadow focus-visible:ring-2 focus-visible:outline-none ${
        destaque
          ? 'border-marca-600/30 bg-marca-100/50 hover:sombra-cartao'
          : 'border-tinta-100 bg-superficie-2 hover:sombra-cartao'
      }`}
    >
      <span
        className={`grid size-9 shrink-0 place-items-center rounded-lg ${
          destaque ? 'bg-marca-600 text-marca-contraste' : 'bg-tinta-100 text-tinta-700'
        }`}
      >
        <Icone className="size-4" aria-hidden />
      </span>
      <span className="min-w-0">
        <span className="text-tinta-900 flex items-center gap-1 font-semibold">
          {titulo}
          <ArrowRight
            className="size-3.5 opacity-0 transition-opacity group-hover:opacity-100"
            aria-hidden
          />
        </span>
        <span className="text-tinta-500 block text-sm">{detalhe}</span>
      </span>
    </Link>
  );
}

const ICONE_PENDENCIA: Record<TipoPendencia, LucideIcon> = {
  EXIGENCIA: AlertTriangle,
  ATO_PARADO: FileClock,
  INTEGRALIZACAO: CalendarClock,
  ASSINATURA: CreditCard,
};

/**
 * As cores da urgência.
 *
 * Três, e o vermelho reservado para o que JÁ passou. Pintar de vermelho o que
 * vence em três semanas é como treinar alguém a ignorar vermelho.
 */
const TOM_URGENCIA: Record<Urgencia, string> = {
  VENCIDO: 'bg-erro-100 text-erro-600',
  PROXIMO: 'bg-alerta-100 text-alerta-600',
  INFORMATIVO: 'bg-tinta-100 text-tinta-700',
};

/**
 * A AGENDA — o que precisa de você.
 *
 * Não é lista de tarefas que alguém digita: cada linha sai de uma coluna do
 * banco (ver `TIPOS_PENDENCIA` no domínio). Agenda que se alimenta de digitação
 * fica desatualizada no primeiro dia corrido, e uma agenda desatualizada é pior
 * que agenda nenhuma — ela mente com autoridade.
 */
export function Agenda({ itens }: { itens: ItemAgenda[] }): JSX.Element {
  if (itens.length === 0) {
    return (
      <div className="border-tinta-100 bg-superficie-2 rounded-xl border p-6 text-center">
        <p className="text-tinta-900 font-medium">Nada exigindo atenção.</p>
        <p className="text-tinta-500 pt-1 text-sm">
          Sem exigência da Junta, rascunho parado ou prazo vencendo.
        </p>
      </div>
    );
  }

  return (
    <ul className="flex flex-col gap-2">
      {itens.map((item, i) => {
        const Icone = ICONE_PENDENCIA[item.tipo];
        const corpo = (
          <>
            <span
              className={`grid size-8 shrink-0 place-items-center rounded-lg ${TOM_URGENCIA[item.urgencia]}`}
            >
              <Icone className="size-4" aria-hidden />
            </span>
            <span className="min-w-0 flex-1">
              {/*
                QUEBRA no celular, corta só a partir de `sm`. A tela estreita
                mostrava "Seu plano vence em br…" — e um título de pendência
                cortado ao meio esconde justamente qual é a pendência. No
                desktop há largura de sobra, e ali o `truncate` volta a valer
                para o nome de sociedade muito longo.
              */}
              <span className="text-tinta-900 block font-medium sm:truncate">
                {item.titulo}
              </span>
              <span className="text-tinta-500 block text-sm">{item.detalhe}</span>
            </span>
            {item.data && (
              <span className="text-tinta-500 shrink-0 text-sm tabular-nums">
                {dataBR(item.data)}
              </span>
            )}
          </>
        );

        const classe =
          'border-tinta-100 bg-superficie-2 flex items-center gap-3 rounded-xl border p-3';

        return (
          <li key={`${item.tipo}-${String(i)}`}>
            {item.para ? (
              <Link
                to={item.para}
                className={`${classe} focus-visible:ring-marca-400 hover:sombra-cartao transition-shadow focus-visible:ring-2 focus-visible:outline-none`}
              >
                {corpo}
              </Link>
            ) : (
              <div className={classe}>{corpo}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Onde o escritório parou. */
export function UltimosAtos({ atos }: { atos: AtoRecente[] }): JSX.Element {
  if (atos.length === 0) {
    return (
      <p className="text-tinta-500 text-sm">
        Nenhum ato ainda. Ele nasce dentro de uma sociedade.
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-1">
      {atos.map((a) => (
        <li key={a.id}>
          <Link
            to={`/sociedades/${a.sociedadeId}`}
            className="hover:bg-superficie-2 focus-visible:ring-marca-400 flex items-center gap-3 rounded-lg px-2 py-2 transition-colors focus-visible:ring-2 focus-visible:outline-none"
          >
            <FileText className="text-tinta-500 size-4 shrink-0" aria-hidden />
            <span className="min-w-0 flex-1">
              <span className="text-tinta-900 block truncate text-sm font-medium">
                {a.sociedadeNome}
              </span>
              <span className="text-tinta-500 block text-xs">
                {NOME_TIPO_ATO[a.tipo]} · {ROTULO_SITUACAO_ATO[a.situacao]}
              </span>
            </span>
            <span className="text-tinta-500 shrink-0 text-xs tabular-nums">
              {dataBR(a.data)}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** As últimas sociedades cadastradas — o caminho de volta ao que se mexeu. */
export function UltimasSociedades({
  sociedades,
}: {
  sociedades: SociedadeRecente[];
}): JSX.Element {
  if (sociedades.length === 0) {
    return <p className="text-tinta-500 text-sm">Nenhuma sociedade cadastrada ainda.</p>;
  }

  return (
    <ul className="flex flex-col gap-1">
      {sociedades.map((s) => (
        <li key={s.id}>
          <Link
            to={`/sociedades/${s.id}`}
            className="hover:bg-superficie-2 focus-visible:ring-marca-400 flex items-center gap-3 rounded-lg px-2 py-2 transition-colors focus-visible:ring-2 focus-visible:outline-none"
          >
            <Building2 className="text-tinta-500 size-4 shrink-0" aria-hidden />
            <span className="min-w-0 flex-1">
              <span className="text-tinta-900 block truncate text-sm font-medium">
                {s.nome}
              </span>
              <span className="text-tinta-500 block text-xs">
                {NOME_TIPO_SOCIETARIO[s.tipo as keyof typeof NOME_TIPO_SOCIETARIO] ??
                  s.tipo}
              </span>
            </span>
            <span className="text-tinta-500 shrink-0 text-xs tabular-nums">
              #{s.codigo}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** Moldura dos dois blocos de baixo. */
export function Bloco({
  titulo,
  acao,
  children,
}: {
  titulo: string;
  acao?: { para: string; rotulo: string };
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="border-tinta-100 bg-superficie sombra-cartao flex flex-col gap-3 rounded-xl border p-4">
      <header className="flex items-center justify-between gap-2">
        <h2 className="text-tinta-900 font-semibold">{titulo}</h2>
        {acao && (
          <Link
            to={acao.para}
            className="text-marca-600 hover:text-marca-700 focus-visible:ring-marca-400 rounded text-sm font-medium underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:outline-none"
          >
            {acao.rotulo}
          </Link>
        )}
      </header>
      {children}
    </section>
  );
}
